import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type FeatureKey, useFeatures } from '../lib/features';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/**
 * Feature kill-switch panel — visible only to users in ADMIN_USER_IDS.
 *
 * Each row is a feature flag in the `platform_features` DB table. Flipping
 * the switch immediately PATCHes the API and invalidates the shared feature
 * cache so the change takes effect site-wide within ~30s (server cache TTL)
 * or instantly on full page reload.
 *
 * Pair with:
 *   - apps/api/src/routes/platform/features.ts  (endpoints + cache)
 *   - apps/api/src/plugins/feature-gate.ts      (server-side 404 enforcement)
 *   - apps/web/src/lib/features.tsx             (web-side hook)
 *
 * Route: /admin/features (no ProtectedShell — like other /admin routes).
 */

type FeatureRow = {
  key: FeatureKey;
  title: string;
  description: string;
};

const FEATURE_ROWS: readonly FeatureRow[] = [
  {
    key: 'intake_forms',
    title: 'Intake Forms',
    description:
      'Public-facing builder-style forms that feed submissions into the analyses pipeline. Requires the intake app to be deployed at intake.develio.app.',
  },
  {
    key: 'sso',
    title: 'SSO (SAML)',
    description:
      'Per-org SAML single sign-on. Requires IdP metadata / certificate configuration per tenant.',
  },
  {
    key: 'api_keys',
    title: 'API Keys',
    description:
      'Programmatic API access tokens scoped to an org. Enables the public REST API surface.',
  },
  {
    key: 'webhooks',
    title: 'Webhooks',
    description:
      'Outgoing webhook subscriptions for analysis lifecycle events. Requires HMAC secret rotation flow.',
  },
];

export function AdminFeaturesRoute() {
  const getToken = useSessionTokenGetter();
  const { features, loaded, refresh } = useFeatures();
  const [saving, setSaving] = useState<FeatureKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ensure the latest server state is loaded when landing on this page —
  // the provider's initial fetch may have happened long before.
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(
    async (key: FeatureKey, next: boolean) => {
      setSaving(key);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/platform/features/${key}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ enabled: next }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error ?? `${res.status}`);
        }
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(null);
      }
    },
    [getToken, refresh],
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="mb-8 border-b border-slate-200 pb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          System Administration
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Feature Flags</h1>
        <p className="mt-1 text-sm text-slate-500">
          Global kill-switches. When off, the feature is hidden from every org's nav and the API
          returns 404 for its routes. Changes propagate within ~30 seconds.
        </p>
        <Link to="/admin" className="mt-3 inline-block text-xs text-brand-teal-600 hover:underline">
          ← Back to admin
        </Link>
      </div>

      {error && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {!loaded && <div className="py-12 text-center text-sm text-slate-500">Loading flags…</div>}

      {loaded && (
        <ul className="space-y-3">
          {FEATURE_ROWS.map((row) => {
            const on = features[row.key];
            const isSaving = saving === row.key;
            return (
              <li
                key={row.key}
                className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">{row.title}</h3>
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">
                      {row.key}
                    </code>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{row.description}</p>
                </div>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => toggle(row.key, !on)}
                  aria-pressed={on}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-wait disabled:opacity-50 ${
                    on ? 'bg-brand-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span className="sr-only">
                    {on ? 'Disable' : 'Enable'} {row.title}
                  </span>
                  <span
                    aria-hidden
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      on ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
